import { useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  orderBy,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useFirestoreCollection } from "../../lib/useFirestoreCollection";
import { CLIENTS_COLLECTION } from "../../lib/portalConfig";
import styles from "./ClientsPage.module.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ClientsPage() {
  const { items } = useFirestoreCollection(CLIENTS_COLLECTION, orderBy("createdAt", "desc"));
  const [form, setForm] = useState({ email: "", name: "", company: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  function update(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    const email = form.email.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) {
      setError("Ingresa un correo válido.");
      return;
    }
    if (items.some((c) => (c.email || "").toLowerCase() === email)) {
      setError("Ese correo ya tiene acceso.");
      return;
    }
    setBusy(true);
    try {
      await addDoc(collection(db, CLIENTS_COLLECTION), {
        email,
        name: form.name.trim(),
        company: form.company.trim(),
        active: true,
        createdAt: serverTimestamp(),
      });
      setForm({ email: "", name: "", company: "" });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(c) {
    try {
      await updateDoc(doc(db, CLIENTS_COLLECTION, c.id), { active: !(c.active !== false) });
    } catch (err) {
      setError(err.message);
    }
  }

  async function toggleSchedule(c) {
    try {
      await updateDoc(doc(db, CLIENTS_COLLECTION, c.id), { showSchedule: !c.showSchedule });
    } catch (err) {
      setError(err.message);
    }
  }

  async function toggleLogistics(c) {
    try {
      await updateDoc(doc(db, CLIENTS_COLLECTION, c.id), { showLogistics: !c.showLogistics });
    } catch (err) {
      setError(err.message);
    }
  }

  async function remove(c) {
    if (!confirm(`¿Quitar el acceso de ${c.email}?`)) return;
    try {
      await deleteDoc(doc(db, CLIENTS_COLLECTION, c.id));
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <section style={{ display: "grid", gap: "1.5rem" }}>
      <header>
        <span className="mono-tag">// 04 — Clientes</span>
        <h1
          style={{
            fontSize: "var(--step-3)",
            fontWeight: 300,
            letterSpacing: "-0.02em",
            marginTop: "0.5rem",
          }}
        >
          Acceso de <strong>clientes</strong>
          <span style={{ color: "var(--color-accent)" }}>.</span>
        </h1>
        <p style={{ color: "var(--color-ink-soft)", marginTop: "0.5rem", maxWidth: "65ch" }}>
          Da de alta los correos autorizados para entrar al portal y seguir sus
          proyectos. Los clientes no crean cuenta: inician sesión con Google y
          solo acceden los correos registrados aquí.
        </p>
      </header>

      <form className={styles.form} onSubmit={onSubmit}>
        <div className={styles.fields}>
          <label className={styles.field}>
            <span>Correo *</span>
            <input
              type="email"
              value={form.email}
              onChange={update("email")}
              placeholder="cliente@empresa.com"
              required
            />
          </label>
          <label className={styles.field}>
            <span>Nombre</span>
            <input
              type="text"
              value={form.name}
              onChange={update("name")}
              placeholder="Nombre del contacto"
            />
          </label>
          <label className={styles.field}>
            <span>Empresa</span>
            <input
              type="text"
              value={form.company}
              onChange={update("company")}
              placeholder="Organización"
            />
          </label>
        </div>
        <button type="submit" className={styles.add} disabled={busy}>
          {busy ? "Agregando…" : "Dar acceso"}
        </button>
        {error && <p className={styles.error}>// {error}</p>}
      </form>

      <div className={styles.list}>
        {items.length === 0 ? (
          <p className="mono-tag">// aún no hay clientes con acceso</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Correo</th>
                <th>Nombre</th>
                <th>Empresa</th>
                <th>Cronograma</th>
                <th>Logística</th>
                <th>Estado</th>
                <th aria-label="acciones" />
              </tr>
            </thead>
            <tbody>
              {items.map((c) => {
                const active = c.active !== false;
                return (
                  <tr key={c.id} data-inactive={!active}>
                    <td className={styles.mono}>{c.email}</td>
                    <td>{c.name || "—"}</td>
                    <td>{c.company || "—"}</td>
                    <td>
                      <button
                        className={styles.badge}
                        data-active={c.showSchedule}
                        onClick={() => toggleSchedule(c)}
                        title="Mostrar / Ocultar cronograma en portal"
                      >
                        {c.showSchedule ? "Visible" : "Oculto"}
                      </button>
                    </td>
                    <td>
                      <button
                        className={styles.badge}
                        data-active={c.showLogistics}
                        onClick={() => toggleLogistics(c)}
                        title="Mostrar / Ocultar logística en portal"
                      >
                        {c.showLogistics ? "Visible" : "Oculto"}
                      </button>
                    </td>
                    <td>
                      <button
                        className={styles.badge}
                        data-active={active}
                        onClick={() => toggleActive(c)}
                        title="Activar / desactivar"
                      >
                        {active ? "Activo" : "Inactivo"}
                      </button>
                    </td>
                    <td className={styles.actionsCell}>
                      <button className={styles.remove} onClick={() => remove(c)}>
                        Quitar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
